if (!customElements.get('irl-looks-slider')) {
  class IrlLooksSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.drawer = this.querySelector('[data-irl-drawer]');
      this.drawerBody = this.querySelector('[data-irl-drawer-body]');
      this.drawerOverlay = this.querySelector('[data-irl-drawer-overlay]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDrawerOpened = this.handleDrawerOpened.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);

      this.addEventListener('click', this.handleClick, { signal: this.signal });
      document.addEventListener('keydown', this.handleKeydown, { signal: this.signal });
      document.addEventListener('irl:drawer-opened', this.handleDrawerOpened, { signal: this.signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal: this.signal });
    }

    disconnectedCallback() {
      this.closeDrawer(false, true);
      this.abortController?.abort();
      this.initialized = false;
    }

    handleClick(event) {
      const hotspot = event.target.closest('[data-irl-hotspot]');
      if (hotspot && this.contains(hotspot)) {
        event.preventDefault();
        event.stopPropagation();
        this.openDrawer(hotspot);
        return;
      }

      if (event.target.closest('[data-irl-drawer-close]') || event.target === this.drawerOverlay) {
        this.closeDrawer(true);
        return;
      }

      const option = event.target.closest('[data-option-value]');
      if (option && this.drawerBody.contains(option)) {
        this.selectOption(option);
        return;
      }

      const addButton = event.target.closest('[data-irl-add]');
      if (addButton && this.drawerBody.contains(addButton)) this.addToCart(addButton);
    }

    openDrawer(opener) {
      const template = this.querySelector(`template[data-irl-product-template="${CSS.escape(opener.dataset.productId)}"]`);
      if (!template) return;

      document.dispatchEvent(new CustomEvent('irl:drawer-opened', { detail: { owner: this } }));
      this.drawerBody.replaceChildren(template.content.cloneNode(true));
      this.opener = opener;
      this.drawerOpen = true;
      opener.setAttribute('aria-expanded', 'true');
      this.initializeProducts();
      this.drawer.classList.add('is-open');
      this.drawerOverlay.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOverlay.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('irl-drawer-open');
      document.body.classList.add('irl-drawer-open');

      window.requestAnimationFrame(() => {
        this.drawer.querySelector('[data-irl-drawer-close]')?.focus();
      });
    }

    closeDrawer(returnFocus = true, immediate = false) {
      if (!this.drawerOpen) return;
      const opener = this.opener;
      this.drawerOpen = false;
      opener?.setAttribute('aria-expanded', 'false');
      this.drawer.classList.remove('is-open');
      this.drawerOverlay.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOverlay.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('irl-drawer-open');
      document.body.classList.remove('irl-drawer-open');
      this.opener = null;
      window.setTimeout(() => {
        if (!this.drawerOpen) this.drawerBody.replaceChildren();
      }, immediate ? 0 : 360);
      if (returnFocus) opener?.focus();
    }

    handleDrawerOpened(event) {
      if (event.detail?.owner !== this) this.closeDrawer(false);
    }

    handleKeydown(event) {
      if (!this.drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    initializeProducts() {
      this.drawerBody.querySelectorAll('[data-irl-product]').forEach((card) => {
        this.showError(card);
        this.initGallery(card);

        try {
          card.productData = JSON.parse(card.querySelector('[data-irl-product-json]').textContent);
          const variants = Array.isArray(card.productData.variants) ? card.productData.variants : [];
          if (!variants.length) throw new Error('Product has no variants.');

          card.selectedVariant = variants.find((variant) => variant.available) || variants[0] || null;
          this.selectVariantOptions(card, card.selectedVariant);
          this.updateProduct(card);
        } catch (error) {
          console.error('Unable to initialize Shop the Look product.', error);
          card.selectedVariant = null;
          this.setProductState(card, null);
          this.showError(card, 'Unable to load this product. Please try again.');
        }
      });
    }

    initGallery(card) {
      const gallery = card.querySelector('[data-irl-gallery]');
      if (!gallery) return;
      const track = gallery.querySelector('[data-irl-gallery-track]');
      const items = [...gallery.querySelectorAll('.irl-looks__gallery-item')];
      const prevButton = gallery.querySelector('[data-irl-gallery-prev]');
      const nextButton = gallery.querySelector('[data-irl-gallery-next]');
      const dots = [...gallery.querySelectorAll('[data-irl-gallery-dot]')];
      if (!track || items.length < 2) return;

      const step = () => Math.abs(items[1].offsetLeft - items[0].offsetLeft);
      const currentIndex = () => Math.round(track.scrollLeft / step());

      const goTo = (index) => {
        const clamped = Math.max(0, Math.min(items.length - 1, index));
        track.scrollTo({ left: items[clamped].offsetLeft, behavior: 'smooth' });
      };

      const updateState = () => {
        const index = currentIndex();
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        if (prevButton) prevButton.disabled = index <= 0;
        if (nextButton) nextButton.disabled = index >= items.length - 1;
      };

      prevButton?.addEventListener('click', () => goTo(currentIndex() - 1));
      nextButton?.addEventListener('click', () => goTo(currentIndex() + 1));
      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => goTo(dotIndex)));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateState), { passive: true });

      updateState();
    }

    variantOptions(variant) {
      return Array.isArray(variant?.options) ? variant.options.map(String) : [];
    }

    findVariant(variants, selectedOptions) {
      const normalizedOptions = selectedOptions.map(String);
      return variants.find((variant) => {
        const options = this.variantOptions(variant);
        return options.length === normalizedOptions.length &&
          normalizedOptions.every((value, index) => options[index] === value);
      }) || null;
    }

    selectVariantOptions(card, variant) {
      if (!variant) return;
      const options = this.variantOptions(variant);
      card.querySelectorAll('[data-option-index]').forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const selected = String(button.dataset.optionValue) === options[optionIndex];
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      });
    }

    selectOption(button) {
      const card = button.closest('[data-irl-product]');
      const optionGroup = button.closest('[data-option-index]');
      optionGroup.querySelectorAll('[data-option-value]').forEach((valueButton) => {
        const selected = valueButton === button;
        valueButton.classList.toggle('is-selected', selected);
        valueButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.updateProduct(card);
    }

    selectedOptions(card) {
      return [...card.querySelectorAll('[data-option-index]')].map((group) => (
        group.querySelector('[data-option-value].is-selected')?.dataset.optionValue || ''
      ));
    }

    updateProduct(card) {
      const selectedOptions = this.selectedOptions(card);
      const variants = card.productData.variants || [];
      const variant = selectedOptions.length
        ? this.findVariant(variants, selectedOptions)
        : card.selectedVariant;
      card.selectedVariant = variant || null;

      card.querySelectorAll('[data-option-index]').forEach((group) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
        });
      });

      this.setProductState(card, variant);

      if (variant) {
        const price = card.querySelector('[data-irl-price]');
        const compare = card.querySelector('[data-irl-compare]');
        const moneyFormat = card.dataset.moneyFormat;
        if (price) price.textContent = this.formatMoney(variant.price, moneyFormat);
        if (compare) {
          const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !showCompare;
          compare.textContent = showCompare ? this.formatMoney(variant.compare_at_price, moneyFormat) : '';
        }
      }
      this.showError(card, variant ? '' : 'This product is temporarily unavailable.');
    }

    setProductState(card, variant) {
      const addButton = card.querySelector('[data-irl-add]');
      const addLabel = card.querySelector('[data-irl-add-label]');
      const labels = card.dataset;
      const state = !variant ? 'unavailable' : variant.available ? 'available' : 'sold-out';
      addButton.disabled = state !== 'available';
      addButton.dataset.variantId = variant ? String(variant.id) : '';
      addLabel.textContent = state === 'available'
        ? labels.addLabel
        : state === 'sold-out'
          ? labels.soldOutLabel
          : labels.unavailableLabel;
    }

    formatMoney(cents, format) {
      const value = Number(cents || 0);
      const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      let amount;
      if (placeholder.includes('no_decimals')) {
        amount = Math.round(value / 100).toLocaleString();
      } else {
        amount = (value / 100).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    async addToCart(button) {
      const card = button.closest('[data-irl-product]');
      const variant = card.selectedVariant;
      if (!variant?.available || button.disabled) return;
      const labels = card.dataset;
      const label = button.querySelector('[data-irl-add-label]');
      button.disabled = true;
      button.classList.add('is-loading');
      label.textContent = labels.addingLabel;
      this.showError(card);

      const components = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter(Boolean);
      const sectionIds = components.map((component) => component.dataset.sectionId).filter(Boolean);
      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', 1);
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item.');
        }

        if (result.sections && components.length === Object.keys(result.sections).length) {
          components.forEach((component) => {
            if (typeof component.updateFromCartChange === 'function') {
              component.updateFromCartChange(result.sections[component.dataset.sectionId]);
            }
          });
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: result.variant_id || variant.id }
        }));
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        label.textContent = labels.addedLabel;

        if (theme.settings.afterAddToCart === 'drawer' && document.querySelector('.js-cart-drawer')) {
          this.closeDrawer(false);
          window.setTimeout(() => {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          }, 360);
        } else if (theme.settings.afterAddToCart === 'notification') {
          const notificationTemplate = document.getElementById('AddedNotification');
          if (notificationTemplate?.content?.firstElementChild) {
            const notification = notificationTemplate.content.firstElementChild.cloneNode(true);
            notification.dataset.productTitle = result.product_title || card.productData.title;
            (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
          }
        }

        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.classList.remove('is-success');
          button.disabled = false;
          label.textContent = labels.addLabel;
        }, 2000);
      } catch (error) {
        button.classList.remove('is-loading', 'is-success');
        button.disabled = false;
        label.textContent = labels.addLabel;
        this.showError(card, error.message || 'Unable to add this item. Please try again.');
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      }
    }

    showError(card, message = '') {
      const error = card.querySelector('[data-irl-error]');
      if (!error) return;
      error.hidden = !message;
      error.textContent = message;
    }

    handleBlockSelect(event) {
      const slide = event.target.closest('.irl-looks__slide');
      if (!slide || !this.contains(slide)) return;
      slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('irl-looks-slider', IrlLooksSlider);
}
